import React, { useEffect, useState, useRef } from "react";
import { Button, Col, Form, Row, Select, Tooltip, Tour } from "antd";
import { VideoCameraOutlined } from "@ant-design/icons";
import FormItem from "antd/es/form/FormItem";
import FilterMap from "../../FilterMap";
import tutorialVideo from "../../../videos/explore_practices.mp4";
import "../style.css";
import { useDispatch, useSelector } from "react-redux";
import { nextStep, setActiveDropdown } from "../../../Redux/tutorialSlice";
import tutorialSteps from "../../Tutorial/tutorialSteps";
import useCutout from "../../Tutorial/useCutout";
import {
  setSelectedCounty,
  setSelectedFips,
  setSelectedState,
} from "../../../Redux/filterSlice";
import { fetchGeoCountiesByState } from "../../../Redux/geo/geoThunk";
import { fetchAllPracticeDataByCounty } from "../../../Redux/practice/practiceThunk";

const Step1 = () => {
  const inputRef = useRef(null);
  const [form] = Form.useForm();
  const [startTour, setStartTour] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useDispatch();
  const { updateCutout } = useCutout();
  const { activeDropdown, currentStep, isTutorialActive } = useSelector(
    (state) => state.tutorial
  );
  const { selectedState, selectedCounty } = useSelector(
    (state) => state.filter
  );
  const { stateList, countyList, countyListData } = useSelector(
    (state) => state.geo
  );

  const handleDropdownOpen = (open, selector) => {
    setIsOpen(open);

    if (isTutorialActive) {
      if (open) {
        dispatch(setActiveDropdown(selector));
        updateCutout(selector, "custom", {
          isOpen: true,
          manageLoading: false,
        });
      } else {
        dispatch(setActiveDropdown(null));
        updateCutout(selector, "custom", {
          isOpen: false,
          manageLoading: false,
        });
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("disable-scroll");
      const preventScroll = (e) => e.preventDefault();
      document.addEventListener("touchmove", preventScroll, { passive: false });

      return () => {
        document.body.classList.remove("disable-scroll");
        document.removeEventListener("touchmove", preventScroll);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isTutorialActive) return;

    const step = tutorialSteps[currentStep];
    if (!step || !step.target) return;

    updateCutout(step.target, step.advanceType, {
      isOpen: false,
      manageLoading: true,
    });
  }, [currentStep, isTutorialActive]);

  useEffect(() => {
    if (!isTutorialActive) return;

    if (activeDropdown && !selectedState) {
      updateCutout(activeDropdown, "custom", {
        isOpen: true,
        manageLoading: false,
      });
    } else if (!selectedState) {
      updateCutout(tutorialSteps[currentStep]?.target, "custom", {
        isOpen: false,
        manageLoading: true,
      });
    }
  }, [activeDropdown]);

  const handleStateChange = (value) => {
    dispatch(setSelectedState(value));
    dispatch(setSelectedCounty(null));
    dispatch(fetchGeoCountiesByState(value));

    if (isTutorialActive) {
      dispatch(nextStep());
    }
  };

  const handleCountyChange = (value) => {
    dispatch(setSelectedCounty(value));

    const matchedCounty = countyListData.find(
      (county) => county.cname === value
    );

    if (matchedCounty?.fips) {
      dispatch(setSelectedFips(String(matchedCounty.fips).padStart(5, "0")));
    } else {
      dispatch(setSelectedFips(null));
    }

    if (selectedState) {
      dispatch(
        fetchAllPracticeDataByCounty({
          state: selectedState,
          county: value,
        })
      );
    }

    if (isTutorialActive) {
      dispatch(nextStep());
    }
  };

  return (
    <>
      <Tooltip title="Watch the video tutorial">
        <Button
          type="primary"
          onClick={() => setStartTour(true)}
          style={{ float: "right" }}
        >
          <VideoCameraOutlined />
        </Button>
      </Tooltip>
      <p>
        <br />
        <b>
          1) Available practices and practice standards vary by state. Please
          select your state and county to begin.
        </b>
        <br />
        <br />
      </p>

      <Form ref={inputRef} form={form}>
        <Row>
          <Col span={12}>
            <Form.Item label="State">
              <Select
                className="select-font-size state-sel-tutorial"
                showSearch
                defaultValue={selectedState}
                placeholder="Select State"
                onChange={handleStateChange}
                options={stateList}
                value={selectedState}
                onDropdownVisibleChange={(open) =>
                  handleDropdownOpen(open, ".state-sel-tutorial")
                }
              />
            </Form.Item>
          </Col>

          <Col span={10} offset="1">
            <FormItem label="County">
              <Select
                className="select-font-size county-sel-tutorial"
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                placeholder="Select County"
                defaultValue={selectedCounty}
                onChange={handleCountyChange}
                options={countyList}
                value={selectedCounty}
                onFocus={(e) => e.preventDefault()}
                onDropdownVisibleChange={(open) =>
                  handleDropdownOpen(open, ".county-sel-tutorial")
                }
              />
            </FormItem>
          </Col>
        </Row>
        <Row>
          <div className="filter-map">
            <br />
            <FilterMap />
          </div>
        </Row>
      </Form>

      <Tour
        open={startTour}
        onClose={() => setStartTour(false)}
        steps={[
          {
            title: "Video Tutorial",
            description: "Please follow the steps in this video tutorial",
            cover: (
              <video width="400px" height="300px" controls>
                <source src={tutorialVideo} type="video/mp4" />
              </video>
            ),
            target: () => null,
          },
        ]}
      />
    </>
  );
};

export default Step1;
